// @eidollon/contracts/inference
//
// INTERNAL CONTRACT — services/api ↔ services/inference.
//
// This contract is NOT exposed to the browser. All LLM calls in Eidollon
// route through services/inference per CLAUDE.md Iron Rule 12. The browser
// hits product-shaped endpoints in services/api (e.g. /v1/ai/suggest),
// which translate to /v1/inference/run with a typed task class + template id.
//
// Adding a TaskClass here is a multi-file change: (a) extend the union below,
// (b) add a routing entry in services/inference/src/router.ts, (c) ship a
// matching template in services/inference/src/templates/, (d) declare the
// template's output Zod schema so responses are validated before return.

import { z } from 'zod';

/* ──────────────────────────────────────────────────────────────────────────
 *  Task class — drives model routing (cost-efficient by default)
 *
 *  Routing matrix lives in services/inference/src/router.ts. Default policy:
 *
 *    extract / classify / nl_rewrite → Anthropic Haiku (cheap, schema-bound)
 *    suggest / synthesize           → Anthropic Sonnet
 *    draft                           → Anthropic Opus (the only Opus path)
 *    embed                           → OpenAI text-embedding-3-small
 *
 *  Fallback path is OpenAI when Anthropic returns 5xx or rate-limits.
 * ────────────────────────────────────────────────────────────────────────── */

export const TaskClass = z.enum([
  'extract',
  'classify',
  'nl_rewrite',
  'suggest',
  'synthesize',
  'draft',
  'embed',
  'why_this_matters',
  // translate: per-segment translation through services/translation.
  // Routes to Haiku primary / gpt-4o-mini fallback (extract-class shape).
  // Hot path is fully cache-served from translations + translation_memory;
  // this class only fires on L4 miss. See orchestrators/translation.md.
  'translate',
]);
export type TaskClass = z.infer<typeof TaskClass>;

export const Provider = z.enum(['anthropic', 'openai', 'vertex', 'voyage']);
export type Provider = z.infer<typeof Provider>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Request — POST /v1/inference/run
 * ────────────────────────────────────────────────────────────────────────── */

export const InferenceRequest = z
  .object({
    task: TaskClass,
    template: z.string().min(1).max(64), // template id, e.g. "nl_rewrite.v1"
    // {{var}} bindings. All values string-coerced and length-capped on render.
    vars: z.record(z.string(), z.string().max(8000)).default({}),
    tenantId: z.string().uuid(),
    actorId: z.string().uuid().nullable(), // user id or null for system jobs
    // Optional overrides — recorded in audit; pinning a stronger model
    // requires a documented reason in the calling code.
    modelOverride: z.string().optional(),
    cacheKey: z.string().optional(),
    maxTokens: z.number().int().min(1).max(8192).optional(),
    temperature: z.number().min(0).max(2).optional(),
  })
  .strict();
export type InferenceRequest = z.infer<typeof InferenceRequest>;

export const InferenceUsage = z
  .object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
  })
  .strict();
export type InferenceUsage = z.infer<typeof InferenceUsage>;

export const InferenceMeta = z
  .object({
    provider: Provider,
    model: z.string(),
    templateId: z.string(),
    cached: z.boolean(),
    fallback: z.boolean(), // true when primary provider failed
    latencyMs: z.number().nonnegative(),
    costUsd: z.number().nonnegative(),
    tokens: InferenceUsage,
    requestId: z.string().uuid(),
  })
  .strict();
export type InferenceMeta = z.infer<typeof InferenceMeta>;

export const InferenceResponse = z
  .object({
    // The template defines the output shape; this layer carries it as
    // unknown JSON. Each caller re-parses against the matching template's
    // output schema published in services/inference/src/templates/_registry.ts.
    output: z.unknown(),
    meta: InferenceMeta,
  })
  .strict();
export type InferenceResponse = z.infer<typeof InferenceResponse>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Embed task — typed response (used by personalize worker hot path)
 * ────────────────────────────────────────────────────────────────────────── */

export const EmbedRequest = z
  .object({
    inputs: z.array(z.string().min(1).max(16_000)).min(1).max(128),
    tenantId: z.string().uuid().nullable(), // null for system-wide opp embeds
  })
  .strict();
export type EmbedRequest = z.infer<typeof EmbedRequest>;

export const EmbedResponse = z
  .object({
    vectors: z.array(z.array(z.number())),
    meta: InferenceMeta,
  })
  .strict();
export type EmbedResponse = z.infer<typeof EmbedResponse>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Batch API — async, 50% off list price
 *
 *  Anthropic Message Batches process up to 24h after submission. Use for
 *  scraper-side calls (extract / classify / geocode) where the consumer
 *  can wait. Synchronous /run remains for user-request-bound calls.
 *
 *  Same template allowlist and var-sanitization contract as /run.
 * ────────────────────────────────────────────────────────────────────────── */

export const BatchItem = z
  .object({
    /** Caller-supplied id; echoed on every result row. Must be unique
     *  within the batch — duplicates are a contract bug, not silently
     *  dedup'd. */
    customId: z.string().min(1).max(128),
    task: TaskClass,
    template: z.string().min(1).max(64),
    vars: z.record(z.string(), z.string().max(8000)).default({}),
  })
  .strict();
export type BatchItem = z.infer<typeof BatchItem>;

export const BatchSubmitRequest = z
  .object({
    tenantId: z.string().uuid(),
    actorId: z.string().uuid().nullable(),
    items: z.array(BatchItem).min(1).max(1000),
  })
  .strict();
export type BatchSubmitRequest = z.infer<typeof BatchSubmitRequest>;

export const BatchStatus = z.enum(['in_progress', 'canceling', 'ended']);
export type BatchStatus = z.infer<typeof BatchStatus>;

export const BatchSubmitResponse = z
  .object({
    batchId: z.string(),
    status: BatchStatus,
    itemCount: z.number().int().nonnegative(),
    requestId: z.string().uuid(),
  })
  .strict();
export type BatchSubmitResponse = z.infer<typeof BatchSubmitResponse>;

export const BatchPollResponse = z
  .object({
    batchId: z.string(),
    status: BatchStatus,
    counts: z.object({
      processing: z.number().int().nonnegative(),
      succeeded: z.number().int().nonnegative(),
      errored: z.number().int().nonnegative(),
      canceled: z.number().int().nonnegative(),
      expired: z.number().int().nonnegative(),
    }),
  })
  .strict();
export type BatchPollResponse = z.infer<typeof BatchPollResponse>;

export const BatchResultItemStatus = z.enum(['succeeded', 'errored', 'canceled', 'expired']);
export type BatchResultItemStatus = z.infer<typeof BatchResultItemStatus>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Errors — typed envelope for the inference-specific failure modes
 * ────────────────────────────────────────────────────────────────────────── */

export const InferenceErrorCode = z.enum([
  'budget_exhausted', // tenant daily $ cap hit; HTTP 429
  'rate_limited', // per-tenant token bucket; HTTP 429
  'template_not_found', // template id not in the allowlisted registry; HTTP 400
  'template_var_invalid', // var failed length / sanitization; HTTP 400
  'output_invalid', // model response failed Zod parse; HTTP 502
  'provider_unavailable', // both primary and fallback failed; HTTP 503
  'redaction_failed', // PII/govcon redactor errored; HTTP 500
  'unauthenticated', // shared-secret missing/wrong; HTTP 401
]);
export type InferenceErrorCode = z.infer<typeof InferenceErrorCode>;
